import React from 'react';
import GeneralForm from "../GeneralForm";
import {useOperator} from "context/OperatorContext";
import Page from "components/Page";
import {
  OperatorAudienceAppSettingsData,
  OperatorAudienceSettingsData,
  OperatorBrandingData,
  OperatorGeneralSettingsData,
  OperatorGlossaryData,
  OperatorIntegrationsData,
  OperatorLegalSettingsData, OperatorLocalisationSettingsData,
  OperatorSettingsData,
  OperatorSocialSettingsData
} from "types/Operator";
import {FormikHelpers} from "formik";
import {useMutation} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import BrandingForm from "pages/Settings/BrandingForm";
import LegalForm from '../LegalForm';
import SocialForm from "pages/Settings/SocialForm";
import GlossaryForm from "pages/Settings/GlossaryForm";
import IntegrationsForm from "pages/Settings/IntegrationsForm";
import AudienceAppForm from "pages/Settings/AudienceAppForm";
import AudienceForm from '../AudienceForm';
import LocalisationForm from "../LocalisationForm";

function MutateSettings() {
  const {settings} = useOperator()
  const {notify} = useNotifications()

  const updateSettings = useMutation((data: OperatorSettingsData) => ngage.settings.update(data))

  async function handleUpdate(values: OperatorSettingsData, {setSubmitting}: FormikHelpers<any>) {
    await updateSettings.mutateAsync(values)
    await invalidate('current-operator')

    setSubmitting(false)
  }

  async function handleSubmitGeneral(values: OperatorGeneralSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({general: values}, helpers)
      notify("success", "Settings Updated", `General settings have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `General settings could not be updated`)
      throw error
    }
  }

  async function handleSubmitLocalisation(values: OperatorLocalisationSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({localisation: values}, helpers)
      notify("success", "Settings Updated", `Localisation settings have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Localisation settings could not be updated`)
      throw error
    }
  }

  async function handleSubmitSocial(values: OperatorSocialSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({social: values}, helpers)
      notify("success", "Settings Updated", `Social settings have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Social settings could not be updated`)
      throw error
    }
  }

  async function handleSubmitBranding(values: OperatorBrandingData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({branding: values}, helpers)
      notify("success", "Settings Updated", `Branding settings have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Branding settings could not be updated`)
      throw error
    }
  }

  async function handleSubmitLegal(values: OperatorLegalSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({legal: values}, helpers)
      notify("success", "Settings Updated", `Legal settings have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Legal settings could not be updated`)
      throw error
    }
  }

  async function handleSubmitGlossary(values: OperatorGlossaryData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({glossary: values}, helpers)
      notify("success", "Settings Updated", `Glossary has been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Glossary could not be updated`)
      throw error
    }
  }

  async function handleSubmitAudience(values: OperatorAudienceSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({audience: values}, helpers)
      notify("success", "Settings Updated", `Audience has been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Audience could not be updated`)
      throw error
    }
  }

  async function handleSubmitAudienceApp(values: OperatorAudienceAppSettingsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({audience_app: values}, helpers)
      notify("success", "Settings Updated", `Audience App has been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Audience App could not be updated`)
      throw error
    }
  }

  async function handleSubmitIntegrations(values: OperatorIntegrationsData, helpers: FormikHelpers<any>) {
    try {
      await handleUpdate({integrations: values}, helpers)
      notify("success", "Settings Updated", `Integrations have been updated`)
    } catch (error) {
      notify("error", "Settings Not Updated", `Integrations could not be updated`)
      throw error
    }
  }

  return (
    <Page
      title="Settings"
      subTitle='Customisable settings for the operator platform and app'
      breadcrumbs={{"Admin": "/", "Settings": "/settings"}}
    >
      <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
        {/* General */}
        <GeneralForm settings={settings?.general} onSubmit={handleSubmitGeneral}/>

        {/* Localisation */}
        <LocalisationForm settings={settings?.localisation} onSubmit={handleSubmitLocalisation}/>

        {/* Social */}
        <SocialForm settings={settings?.social} onSubmit={handleSubmitSocial}/>

        {/* Branding */}
        <BrandingForm settings={settings?.branding} onSubmit={handleSubmitBranding}/>

        {/* Legal */}
        <LegalForm settings={settings?.legal} onSubmit={handleSubmitLegal}/>

        {/* Glossary */}
        <GlossaryForm settings={settings?.glossary} onSubmit={handleSubmitGlossary}/>

        {/* Audience */}
        <AudienceForm settings={settings?.audience} onSubmit={handleSubmitAudience}/>

        {/* Audience App */}
        <AudienceAppForm settings={settings?.audience_app} onSubmit={handleSubmitAudienceApp}/>

        {/* Integrations */}
        <IntegrationsForm settings={settings?.integrations} onSubmit={handleSubmitIntegrations}/>
      </div>
    </Page>
  );
}

export default MutateSettings;